import { forwardRef, Fragment, useRef, useState } from 'react';
import type { FunctionComponent, PropsWithoutRef, Ref } from 'react';
import styled, { css } from 'styled-components';

import { defaultThemeProp } from '../../../theme';
import type { ForwardProps, OmitStrict } from '../../../types';
import {
  useConfiguration,
  useConsolidatedRef,
  useFocusWithin,
  useI18n,
  useOuterEvent,
  useTestIds,
  useUID
} from '../../../hooks';
import { cap, createClassName, withTestIds } from '../../../utils';
import FormField from '../../FormField';
import Icon, { registerIcon } from '../../Icon';
import * as calendarIcon from '../../Icon/icons/calendar.icon';
import Flex from '../../Flex';
import { StyledFormControl } from '../../FormControl';
import type { FormControlProps } from '../../FormControl';
import DatePicker from '../Picker/DatePicker';
import type DateTimeProps from '../DateTime.types';
import type { DateTimeCallbackParameter } from '../DateTime.types';
import { getWeekInputTestIds } from '../DateTime.test-ids';
import HiddenText from '../../HiddenText';

import {
  parseToDate,
  getWeekYear,
  getWeek,
  generatePickerButtonAriaLabel,
  convertWeekYearToDate
} from './utils';
import StyledDateTime, {
  BareInput,
  StyledDatePickerDialog,
  StyledDialogContent,
  StyledInputButton,
  StyledInputContainer
} from './DateTime.styles';
import usePickerButton from './usePickerButton';
import PartInput from './PartInput';
import useAutoFocusNextInput from './useAutoFocusNextInput';
import type { DateParts } from './DateTimeInput.types';
import { CALENDAR_TYPES, type CalendarType } from './local';

registerIcon(calendarIcon);

export interface WeekInputProps
  extends OmitStrict<FormControlProps, 'value' | 'defaultValue'>,
    DateTimeProps {}

export const StyledWeekInput = styled(StyledDateTime)<WeekInputProps>(props => {
  const {
    theme: { base }
  } = props;

  return css`
    padding-inline-end: 0;

    ${StyledInputContainer} > ${BareInput}:first-of-type {
      margin-inline-start: ${base.spacing};
    }
  `;
});

StyledWeekInput.defaultProps = defaultThemeProp;

const convertToCallbackParameter = (
  date: Date,
  parts: DateParts<string | undefined>,
  calendarType?: CalendarType
): DateTimeCallbackParameter => {
  if ([parts.week, parts.year].every(Boolean)) {
    const { year, month, day } = convertWeekYearToDate(parts, calendarType) ?? {};
    date.setUTCFullYear(Number(year), Number(month), Number(day));
    return {
      valueAsISOString: date.toISOString(),
      valueAsTimestamp: date.getTime()
    };
  }
  if ([parts.week, parts.year].every(p => !p)) {
    return {
      valueAsISOString: undefined,
      valueAsTimestamp: undefined
    };
  }
  return {
    valueAsISOString: '',
    valueAsTimestamp: NaN,
    state: 'incomplete'
  };
};

const WeekInput: FunctionComponent<WeekInputProps & ForwardProps> = forwardRef(function WeekInput(
  props: PropsWithoutRef<WeekInputProps>,
  ref: Ref<HTMLFieldSetElement>
) {
  const {
    testId,
    value,
    min,
    max,
    id,
    label,
    labelHidden,
    status,
    info,
    required,
    readOnly,
    disabled,
    onChange,
    onFocus,
    onBlur,
    autoFocus,
    additionalInfo,
    className,
    ...restProps
  } = props;

  const testIds = useTestIds(testId, getWeekInputTestIds);

  const { locale } = useConfiguration();
  const t = useI18n();
  const containerRef = useConsolidatedRef(ref);
  const yearPartId = useUID();

  const date = value !== undefined ? parseToDate(value) : undefined;
  const [hasValue, setHasValue] = useState(!!date);

  // derive calendarType from locale
  // supporting only (mon=1/sun=7) as of now, support for rest of the days is captured in ENHANCEMENT-1906
  let calendarType: CalendarType = CALENDAR_TYPES.ISO_8601;
  if (locale?.includes('-fw-sun')) calendarType = CALENDAR_TYPES.US;
  else if (locale?.includes('-fw-mon')) calendarType = CALENDAR_TYPES.ISO_8601;

  const minYear = min !== undefined ? parseToDate(min).getUTCFullYear() : 1;
  const maxYear = max !== undefined ? parseToDate(max).getUTCFullYear() : 9999;

  const [weekInputRef, yearInputRef] = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null)
  ];

  const { description, descriptionID } = useAutoFocusNextInput([weekInputRef, yearInputRef]);

  const pickParts = (): DateParts<string | undefined> => {
    return {
      week: weekInputRef.current?.value || undefined,
      year: yearInputRef.current?.value || undefined
    };
  };

  const currentlySelectedDate = () => {
    const dateParts = convertWeekYearToDate(pickParts(), calendarType);
    if (dateParts) {
      const year = Number(dateParts.year);
      const month = Number(dateParts.month);
      const day = Number(dateParts.day);

      const current = date ?? new Date();
      return new Date(
        current.setUTCFullYear(
          year ?? current.getUTCFullYear(),
          month ?? current.getUTCMonth(),
          day ?? current.getUTCDate()
        )
      );
    }
  };

  const inputContainerRef = useRef<HTMLDivElement>(null);

  const onFocusChange = (focused: boolean) => {
    const callbackParam = convertToCallbackParameter(
      date ? new Date(date) : new Date(),
      pickParts(),
      calendarType
    );
    if (onFocus && focused) onFocus(callbackParam);
    if (!focused) {
      if (inputContainerRef.current) inputContainerRef.current.scrollTop = 0;
      onBlur?.(callbackParam);
    }
  };

  const [open, setOpen, , setPopoverRef, buttonEl, setButtonEl] = usePickerButton();

  const onInputChange = () => {
    onChange?.(
      convertToCallbackParameter(date ? new Date(date) : new Date(), pickParts(), calendarType)
    );
    buttonEl?.setAttribute(
      'aria-label',
      generatePickerButtonAriaLabel(currentlySelectedDate(), 'week', locale, t, open)
    );
    if (weekInputRef.current?.value !== '' && yearInputRef.current?.value !== '') {
      setHasValue(true);
    } else {
      setHasValue(false);
    }
  };

  // autoclose picker on focusing out
  useOuterEvent('focusin', [containerRef], e => {
    if (containerRef.current?.contains(e.relatedTarget as HTMLElement)) setOpen(false);
  });

  const displayNames = new Intl.DisplayNames(locale, {
    style: 'long',
    type: 'dateTimeField'
  }) as Intl.DateTimeDisplayNames;

  const WeekNumberInput = (
    <PartInput
      data-testid={testIds.controlWeek}
      key='week'
      placeholder={t('week_placeholder')}
      aria-label={displayNames.of('weekOfYear')}
      value={date ? getWeek(date, calendarType, 'UTC') : undefined}
      onChange={onInputChange}
      size={2}
      maxLength={2}
      min={1}
      max={53}
      readOnly={readOnly}
      ref={weekInputRef}
      autoFocus={autoFocus}
    />
  );

  const YearInput = (
    <Fragment key='year'>
      <PartInput
        data-testid={testIds.controlYear}
        placeholder={t('year_placeholder')}
        aria-label={displayNames.of('year')}
        aria-describedby={yearPartId}
        value={date ? getWeekYear(date, calendarType, 'UTC') : undefined}
        onChange={onInputChange}
        size={4}
        maxLength={4}
        min={minYear}
        max={maxYear}
        readOnly={readOnly}
        ref={yearInputRef}
      />
      <HiddenText id={yearPartId}>{t('year_input_description')}</HiddenText>
    </Fragment>
  );

  useFocusWithin([containerRef], onFocusChange);

  const Picker = (
    <>
      <StyledInputButton
        data-testid={testIds.openClosePicker}
        variant='simple'
        icon
        ref={setButtonEl}
        onClick={() => setOpen(cur => !cur)}
        aria-label={generatePickerButtonAriaLabel(currentlySelectedDate(), 'week', locale, t, open)}
        readOnly={readOnly}
        disabled={disabled}
      >
        <Icon name='calendar' />
      </StyledInputButton>
      {open && buttonEl && (
        <StyledDatePickerDialog ref={setPopoverRef} target={buttonEl} aria-label={t('choose_week')}>
          <Flex container={{ direction: 'column' }} as={StyledDialogContent}>
            <DatePicker
              testId={testIds.picker}
              selected={currentlySelectedDate()}
              calendarType={calendarType}
              onChange={selected => {
                weekInputRef.current!.value =
                  (selected &&
                    getWeek(selected, calendarType, 'UTC').toString().padStart(2, '0')) ||
                  '';
                yearInputRef.current!.value =
                  (selected && getWeekYear(selected, calendarType, 'UTC').toString()) || '';
                setOpen(false);
                setTimeout(() => buttonEl?.focus());
                onInputChange();
              }}
              min={min}
              max={max}
              mode='week'
            />
          </Flex>
        </StyledDatePickerDialog>
      )}
    </>
  );

  const Comp = (
    <Flex
      data-testid={label ? undefined : testIds.root}
      as={StyledWeekInput}
      forwardedAs={StyledFormControl}
      container={{ alignItems: 'center', wrap: 'nowrap', justify: 'between' }}
      status={status}
      readOnly={readOnly}
      disabled={disabled}
      ref={containerRef}
      onClick={(e: MouseEvent) => {
        if (e.target === containerRef.current) weekInputRef.current?.focus();
      }}
      {...restProps}
      className={createClassName('week-input', className)}
    >
      <Flex
        ref={inputContainerRef}
        as={StyledInputContainer}
        container={{ alignItems: 'center', wrap: 'wrap' }}
      >
        {cap(displayNames.of('weekOfYear'))} {WeekNumberInput},{YearInput}
      </Flex>
      {!readOnly && (
        <Flex container>
          {hasValue && (
            <StyledInputButton
              data-testid={testIds.clearCurrentWeek}
              aria-label={t('clear_current_date')}
              icon
              onClick={() => {
                weekInputRef.current!.value = '';
                yearInputRef.current!.value = '';
                setTimeout(() => buttonEl?.focus());
                onInputChange();
              }}
              variant='simple'
              compact
              label={t('clear')}
            >
              <Icon name='times' />
            </StyledInputButton>
          )}
          {Picker}
        </Flex>
      )}
    </Flex>
  );

  return label ? (
    <>
      <FormField
        testId={testIds}
        as='fieldset'
        label={label}
        labelHidden={labelHidden}
        id={id}
        info={info}
        readOnly={readOnly}
        status={status}
        required={required}
        disabled={disabled}
        additionalInfo={additionalInfo}
        aria-describedby={descriptionID}
        ref={ref}
      >
        {Comp}
      </FormField>
      {description}
    </>
  ) : (
    Comp
  );
});

export default withTestIds(WeekInput, getWeekInputTestIds);
